import { useEffect, useMemo, useState } from 'react'
import Cookies from 'js-cookie'
import { DEFAULT_THEME, Theme } from '@/types/theme'
import { resolveTenant } from '@/utils/tenantTheme'
import { loadTenantTheme } from '@/api/loadTenantTheme'
import { buildManifest } from '@/utils/pwaManifest'
import { ensureContrast, mixHex, pickOnColor } from '@/utils/contrast'
import { useThemeStore } from '@/store/theme'
import { useChannelStore } from '@/store/channel'
import { VENDURE_LANGUAGE_CODE_HEADER_KEY } from '@/config'
import { FALLBACK_LANGUAGE_CODE, fromUrlLang, toCatalogCode } from '@/i18n/languages'
import { getLangPrefix } from '@/utils/localizedPath'
import { loadLanguage } from '@/i18n'

export function useTenantId(): string | null {
  return useMemo(() => resolveTenant(), [])
}

type ChannelLoaderStatus = 'idle' | 'loading' | 'ready' | 'unavailable'

export interface ChannelLoaderValue {
  tenantTheme: Theme
  isLoading: boolean
  isUnavailable: boolean
  status: ChannelLoaderStatus
}

export function useChannelLoader(tenantId: string | null): ChannelLoaderValue {
  const [tenantTheme, setTenantTheme] = useState<Theme>(DEFAULT_THEME)
  const [status, setStatus] = useState<ChannelLoaderStatus>('idle')

  const applyTenantTheme = useThemeStore((store) => store.applyTenantTheme)
  const setChannelId = useChannelStore((store) => store.setChannelId)
  const setShopIsPublic = useChannelStore((store) => store.setShopIsPublic)
  const setGoogleTagManagerId = useChannelStore((store) => store.setGoogleTagManagerId)
  const setAvailableLanguageCodes = useChannelStore((store) => store.setAvailableLanguageCodes)
  const setDefaultLanguageCode = useChannelStore((store) => store.setDefaultLanguageCode)
  const setCurrentLanguageCode = useChannelStore((store) => store.setCurrentLanguageCode)
  const setDeliveryDays = useChannelStore((store) => store.setDeliveryDays)
  const setPricesIncludeTax = useChannelStore((store) => store.setPricesIncludeTax)
  const setFreeShippingRates = useChannelStore((store) => store.setFreeShippingRates)

  useEffect(() => {
    let cancelled = false

    async function run() {
      setStatus('loading')

      try {
        const result = await loadTenantTheme(tenantId)

        if (cancelled) return

        if (result.status === 'unavailable') {
          setStatus('unavailable')
          return
        }

        const { theme, channelId, shopIsPublic, googleTagManagerId, availableLanguageCodes, defaultLanguageCode, deliveryDays, pricesIncludeTax, freeShippingRates } = result
        const rootHtmlElement: HTMLElement = document.documentElement

        if (!rootHtmlElement) {
          throw new Error("Error while applying app's theme. Could not find (rootHtmlElement).")
        }

        rootHtmlElement.style.setProperty('--tenant-primary', theme.colors.primary)
        rootHtmlElement.style.setProperty('--tenant-secondary', theme.colors.secondary)
        rootHtmlElement.style.setProperty('--tenant-background', theme.colors.background)
        rootHtmlElement.style.setProperty('--tenant-foreground', theme.colors.foreground)
        rootHtmlElement.style.setProperty('--tenant-font-family', theme.font.family)

        // Tenants pick their own brand colors, and plenty of them fail WCAG AA as
        // text. Derive passing text colors here rather than hardcoding darker
        // shades per component, which would only ever be correct for one channel.
        rootHtmlElement.style.setProperty(
          '--tenant-primary-text',
          ensureContrast(theme.colors.primary, theme.colors.background),
        )
        rootHtmlElement.style.setProperty('--tenant-on-primary', pickOnColor(theme.colors.primary))
        rootHtmlElement.style.setProperty(
          '--tenant-muted',
          ensureContrast(
            mixHex(theme.colors.foreground, theme.colors.background, 0.6),
            theme.colors.background,
          ),
        )

        if (theme.faviconUrl) {
          let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
          if (!link) {
            link = document.createElement('link')
            link.rel = 'icon'
            document.head.appendChild(link)
          }
          link.href = theme.faviconUrl
        }

        // The title is per-route, not per-tenant — src/hooks/useSeo.ts owns it and
        // falls back to theme.name (the channel's storeDisplayName) via the theme
        // store. Writing it here too would race with the route's own value.

        let appleTouchIconLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement
        if (!appleTouchIconLink) {
          appleTouchIconLink = document.createElement('link')
          appleTouchIconLink.rel = 'apple-touch-icon'
          document.head.appendChild(appleTouchIconLink)
        }
        appleTouchIconLink.href = theme.pwaIcon192Url || DEFAULT_THEME.pwaIcon192Url || ''

        let themeColorMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement
        if (!themeColorMeta) {
          themeColorMeta = document.createElement('meta')
          themeColorMeta.name = 'theme-color'
          document.head.appendChild(themeColorMeta)
        }
        themeColorMeta.content = theme.colors.primary

        // Manifest can't be a static file (branding differs per domain/tenant), so it's
        // built from the resolved theme and swapped in via a blob URL, same idea as the
        // favicon swap above.
        let manifestLink = document.querySelector("link[rel='manifest']") as HTMLLinkElement
        if (!manifestLink) {
          manifestLink = document.createElement('link')
          manifestLink.rel = 'manifest'
          document.head.appendChild(manifestLink)
        }
        const previousManifestHref = manifestLink.getAttribute('href')
        const manifestBlob = new Blob([JSON.stringify(buildManifest(theme))], { type: 'application/manifest+json' })
        manifestLink.href = URL.createObjectURL(manifestBlob)
        if (previousManifestHref?.startsWith('blob:')) {
          URL.revokeObjectURL(previousManifestHref)
        }

        applyTenantTheme(theme)
        setTenantTheme(theme)
        setChannelId(channelId)
        setShopIsPublic(shopIsPublic)
        if (googleTagManagerId) {
          setGoogleTagManagerId(googleTagManagerId)
        }

        setAvailableLanguageCodes(availableLanguageCodes)
        setDefaultLanguageCode(defaultLanguageCode ?? undefined)
        setDeliveryDays(deliveryDays)
        setPricesIncludeTax(pricesIncludeTax)
        setFreeShippingRates(freeShippingRates)
        // The URL prefix (`/en/...`) wins over the cookie so shared and indexed
        // links open in the language they were made for.
        const urlLanguageCode = fromUrlLang(getLangPrefix(window.location.pathname), availableLanguageCodes)
        const activeLanguageCode =
          urlLanguageCode || Cookies.get(VENDURE_LANGUAGE_CODE_HEADER_KEY) || defaultLanguageCode || FALLBACK_LANGUAGE_CODE
        if (urlLanguageCode) {
          Cookies.set(VENDURE_LANGUAGE_CODE_HEADER_KEY, urlLanguageCode, { expires: 365 })
        }
        setCurrentLanguageCode(activeLanguageCode)
        await loadLanguage(toCatalogCode(activeLanguageCode))

        setStatus('ready')
      } catch (err) {
        console.error('Failed to initialize channel.', err)
        if (!cancelled) {
          setStatus('unavailable')
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [
    tenantId,
    applyTenantTheme,
    setChannelId,
    setShopIsPublic,
    setGoogleTagManagerId,
    setAvailableLanguageCodes,
    setDefaultLanguageCode,
    setCurrentLanguageCode,
    setDeliveryDays,
    setPricesIncludeTax,
    setFreeShippingRates,
  ])

  return {
    tenantTheme,
    isLoading: status === 'loading' || status === 'idle',
    isUnavailable: status === 'unavailable',
    status,
  }
}
